/**
 * PLAT-11059 — how a passage of transcript is rendered for a model to read.
 *
 * Extracted from `useAITextBaseText` (PLAT-10913), unchanged, so the reel's
 * naming base text and the Smart Graphics base text are one shape rather than
 * two: a prompt is told what its input looks like (PLAT-11058 DS5), and two
 * writers of that input would be two shapes to keep a single description true
 * to. Pure — no React, no MobX, no store access.
 */

/**
 * The parts of a transcript token these functions read. Structural on purpose:
 * the clip editor hands out `ActiveTranscriptTokenWithClipSegmentAttributes`,
 * the transcript view hands out `TranscriptElementWithViewData`, and neither is
 * the other.
 */
export interface SpeakerTurnToken {
  type?: string;
  value?: string | null;
  /** Internal `spk_X` label — never shown to the model, only counted (DS6). */
  speaker_label?: string;
}

/**
 * Values joined into readable text, with no speaker labels.
 *
 * A space is inserted before each word and punctuation is butted against the
 * word it follows — `deriveReelSections.firstLineOf`'s rule, and it is load
 * bearing: whitespace tokens do **not** survive re-projection into a clip
 * (`trimTextAndFilterOutWhitespaces` strips them), so concatenating raw values
 * runs every word of a reel together. Where whitespace tokens *are* present (the
 * transcript view's elements), they butt like any other punctuation and the
 * doubled space they leave is collapsed below — so both inputs read the same.
 */
export function joinValues(elements: ReadonlyArray<SpeakerTurnToken>): string {
  let text = '';
  for (const el of elements) {
    const value = el.value ?? '';
    if (!value) continue;
    if (el.type === 'text' && text) text += ' ';
    text += value;
  }
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Join as speaker turns: one line per consecutive run of the same
 * `speaker_label`, prefixed `Speaker N:` in first-appearance order.
 *
 * Ordinals rather than the internal `spk_X` label (which is meaningless to a
 * model) and rather than resolved names (which would drag the speaker map into
 * a resolver whose only job is words). With a single speaker the labels carry
 * no information, so they are left off entirely.
 *
 * `ordinalsFor` numbers the speakers from outside this passage — the reel-wide
 * map the chapterization base text builds, so that Speaker 2 in one Section is
 * Speaker 2 in every other. Given one, a passage with a single voice is still
 * labelled: which voice it is, is the whole point of numbering across passages.
 */
export function joinSpeakerTurns(
  elements: ReadonlyArray<SpeakerTurnToken>,
  ordinalsFor?: ReadonlyMap<string, number>,
): string {
  const ordinals = ordinalsFor ?? speakerOrdinals(elements);
  if (!ordinalsFor && ordinals.size < 2) return joinValues(elements);

  // An element WITHOUT a label (whitespace and punctuation often carry none)
  // continues the current turn rather than opening an unlabelled one — the
  // alternative fragments every sentence into one turn per word.
  const turns: { label: string | undefined; parts: SpeakerTurnToken[] }[] = [];
  for (const el of elements) {
    const last = turns[turns.length - 1];
    const opensTurn =
      !last || (!!el.speaker_label && el.speaker_label !== last.label);
    if (opensTurn) {
      turns.push({ label: el.speaker_label, parts: [el] });
    } else {
      last.parts.push(el);
    }
  }

  return turns
    .map(turn => {
      // The same joining rule inside a turn as outside one, so a passage does
      // not read differently for having a speaker label on it.
      const said = joinValues(turn.parts);
      if (said === '') return '';
      const ordinal = turn.label ? ordinals.get(turn.label) : undefined;
      return ordinal ? `Speaker ${ordinal}: ${said}` : said;
    })
    .filter(line => line !== '')
    .join('\n');
}

/**
 * Speakers numbered in first-appearance order over whatever tokens are given —
 * one passage, or a whole reel of them.
 */
export function speakerOrdinals(
  elements: ReadonlyArray<SpeakerTurnToken>,
): Map<string, number> {
  const ordinals = new Map<string, number>();
  for (const el of elements) {
    const label = el.speaker_label;
    if (label && !ordinals.has(label)) ordinals.set(label, ordinals.size + 1);
  }
  return ordinals;
}
